/* eslint-disable @typescript-eslint/no-empty-function */
'use client';

import { useRef, useState } from 'react';

import { useFeature } from 'flagged';
import queryString from 'query-string';
import dynamic from 'next/dynamic';

import Tab, { TabItem } from '@components/ui/tabNext/Tab';
import { formatArrayToString } from '@utils/utils';
import { callService } from '@api/remote-call';
import { useDevice } from '@contexts/device';
import { WebUrlType } from '@api/v3';
import { useOutsideClick } from '@hooks/hooks';
import { findMatchingDateFields } from '@utils/findMatchingDateFields';
import { productImpression } from '@utils/newDataLayer/productImpression';

import HotelTabContent, { HotelBookerError } from './HotelTabContent';
import FlightTabContent, { FlightBookerError } from './FlightTabContent';
import BookerSearchButton from './common/BookerSearchButton';
import { BookerTabItems } from './common/bookerUtils';
import CruiseTabContent, { CruiseBookerError } from './CruiseTabContent';
import BookerWheretoGo, { TourBookerError } from './common/BookerWheretoGo';
import { CyprusTabContent, PackageBookerError } from './CyprusTabContent';
import {
  BookerContainer,
  HomePageBookerContainer,
  TabsContainer,
} from './styled';

const PackageNearestDateWarning = dynamic(
  () => import('@components/views/components/PackageNearestDateWarning')
);

export const urlByFilterItems = (
  page: string,
  items: {
    [key: string]: string | number | undefined;
  }
) => {
  let url = `${page}?`;
  Object.keys(items).forEach((key) => {
    if (key && items[key]) {
      url += `${key}=${items[key]}&`;
    }
  });
  url = url.slice(0, -1);
  return url;
};

export const BOOKER_TABS = {
  HOTEL: 0,
  CYPRUS: 1,
  TOUR: 2,
  FLIGHT: 3,
  CRUISE: 4,
};

export type BookerProps = {
  hasError?: boolean;
  setIsBookerLoading?: React.Dispatch<React.SetStateAction<boolean>>;
};

const isHotelUrl = (urlType: string) => {
  switch (urlType) {
    case WebUrlType.HOTEL_CHAIN:
    case WebUrlType.HOTEL_CITY:
    case WebUrlType.HOTEL_REGION:
    case WebUrlType.HOTEL_THEME:
    case WebUrlType.HOTEL_LANDING:
      return true;
    default:
      return false;
  }
};

const isTourUrl = (urlType: string) => {
  switch (urlType) {
    case WebUrlType.TOUR_THEME:
    case WebUrlType.TOUR:
    case WebUrlType.TOUR_REGION:
    case WebUrlType.TOUR_CITY:
    case WebUrlType.TOUR_COUNTRY:
    case WebUrlType.TOUR_CATEGORY:
    case WebUrlType.TOUR_CONTINENT:
    case WebUrlType.TOUR_SPECIAL_PERIOD:
    case WebUrlType.TAG:
      return true;
    default:
      return false;
  }
};

const isCruiseUrl = (urlType: string) => {
  switch (urlType) {
    case WebUrlType.CRUISE:
    case WebUrlType.CRUISE_REGION:
    case WebUrlType.CRUISE_PORT:
    case WebUrlType.CRUISE_THEME:
      return true;
    default:
      return false;
  }
};

export const Booker = ({
  hasError = false,
  setIsBookerLoading,
}: BookerProps) => {
  const [href, setHref] = useState<string>('');
  const { isDesktop } = useDevice();
  const hasCyprusFeature = useFeature('cyprus');
  const [
    isPackageNearestDateWarningVisible,
    setIsPackageNearestDateWarningVisible,
  ] = useState(false);

  const [selected, setselected] = useState(BOOKER_TABS.HOTEL);
  const [tabType, setTabType] = useState<string>('');
  const [hotelError, setHotelError] = useState<HotelBookerError | undefined>({
    location: false,
  });
  const [flightError, setFlightError] = useState<
    FlightBookerError | undefined
  >();
  const [packageError, setPackageError] = useState<
    PackageBookerError | undefined
  >();

  const [tourError, setTourError] = useState<TourBookerError | undefined>({
    location: false,
  });
  const [cruiseError, setCruiseError] = useState<CruiseBookerError | undefined>(
    {
      location: false,
    }
  );
  const [hotelGuestError, setHotelGuestError] = useState<boolean>(false);
  const [packageGuestError, setPackageGuestError] = useState<boolean>(false);

  const [initialPackageError, setInitialPackageError] = useState<
    PackageBookerError | undefined
  >();
  const [initialFlightError, setInitialFlightError] = useState<
    FlightBookerError | undefined
  >();
  const [initialHotelError, setInitialHotelError] = useState<
    HotelBookerError | undefined
  >({
    location: true,
  });
  const [initialTourError, setInitialTourError] = useState<
    TourBookerError | undefined
  >({
    location: true,
  });
  const [initialCruiseError, setInitialCruiseError] = useState<
    HotelBookerError | undefined
  >({
    location: true,
  });
  const [isFlightOnSearchClicked, setIsFlightOnSearchClicked] =
    useState<boolean>(false);

  // ===============================================
  // Toggle active tab (hotel , tour , flight)
  // ===============================================
  const onTabSelect = (s: number) => {
    setHotelError({});
    setFlightError({});
    setTourError({});
    setCruiseError({});
    setPackageError({});
    setselected(s);
    setHref('');
  };

  // ===============================================
  // Handle updating the href
  // ===============================================
  const onHrefUpdate = (href: string) => {
    setHref(href);
  };
  // ===============================================
  // Handle Clicking on the search button
  // ===============================================
  const handleSearchButtonClick = async () => {
    setHotelError(initialHotelError);
    setPackageError(initialPackageError);
    setFlightError(initialFlightError);
    setTourError(initialTourError);
    setCruiseError(initialCruiseError);

    const packageNearestDate = findMatchingDateFields({
      in: (queryString.parseUrl(href)?.query?.in as string) ?? '',
      out: (queryString.parseUrl(href)?.query?.out as string) ?? '',
    });

    if (selected === BOOKER_TABS.TOUR && initialTourError?.location) return;
    if (selected === BOOKER_TABS.CRUISE && initialCruiseError?.location) return;

    if (selected === BOOKER_TABS.HOTEL && initialHotelError?.location) return;

    if (hotelGuestError) return;
    if (packageGuestError) return;

    if (
      selected === BOOKER_TABS.FLIGHT &&
      (initialFlightError?.location?.arriving ||
        initialFlightError?.location?.departure ||
        initialFlightError?.date)
    )
      return;

    if (
      selected === BOOKER_TABS.CYPRUS &&
      (initialPackageError?.location?.hotel ||
        initialPackageError?.location?.departure ||
        initialPackageError?.date)
    )
      return;

    if (packageNearestDate.length > 0 && selected === BOOKER_TABS.CYPRUS)
      return setIsPackageNearestDateWarningVisible(true);
    if (href.includes('ic-hat-ucuslari') || href.includes('dis-hat-ucuslari')) {
      setIsBookerLoading?.(true);
      productImpression({
        item_list_location: 'Anasayfa Booker',
        item_list_id: '',
        type: 'flight',
      });
      window.open(href, '_self');
    }
    setIsBookerLoading?.(true);
    setIsFlightOnSearchClicked(true);
    if (
      !href.includes('ic-hat-ucuslari') &&
      !href.includes('dis-hat-ucuslari')
    ) {
      try {
        const data = await callService(
          { service: 'SearchesService', method: 'searchesUrl', version: 'v3' },
          {
            s: formatArrayToString(href.split('?')[0]),
          }
        );

        if (data?.type) {
          setTabType(data.type);

          if (isTourUrl(data.type)) {
            const text =
              data.type === WebUrlType.TOUR_CITY ||
              data.type === WebUrlType.TOUR_REGION
                ? `${data?.text} Turları`
                : `${data?.text}`;
            productImpression({
              item_list_location:
                data.type === 'TourTheme'
                  ? 'Anasayfa Booker Popüler Aramalar'
                  : 'Anasayfa Booker',
              item_list_id: text,
              type: 'tour',
            });
          }

          if (isHotelUrl(data.type)) {
            const text =
              data.type === 'HotelCity' || data.type === 'HotelRegion'
                ? `${data?.text} Otelleri`
                : ' ';
            productImpression({
              item_list_location: 'Anasayfa Booker',
              item_list_id: text,
              type: 'hotel',
            });
          }
          if (data.type === WebUrlType.HOTEL) {
            const text = `${data?.text} Oteli`;
            data?.package
              ? productImpression({
                  item_list_location: 'Anasayfa Booker',
                  item_list_id: text,
                  type: 'package',
                })
              : productImpression({
                  item_list_location: 'Anasayfa Booker',
                  item_list_id: text,
                  type: 'hotel',
                });
          }
          if (data.type === WebUrlType.HOTEL_THEME) {
            const text = `${data?.text}`;
            productImpression({
              item_list_location: 'Anasayfa Booker Popüler Aramalar',
              item_list_id: text,
              type: 'hotel',
            });
          }
          if (isCruiseUrl(data.type)) {
            productImpression({
              item_list_location:
                data.type === 'CruiseTheme'
                  ? 'Anasayfa Booker Popüler Aramalar'
                  : 'Anasayfa Booker',
              item_list_id: data?.text?.includes('Turları')
                ? data?.text
                : `${data?.text} Gemi Turları`,
              type: 'ship',
            });
          }
        }
      } catch (error) {
        setIsBookerLoading?.(false);
      }
    }
  };

  const getSelectedContent = (
    s: number,
    onHrefUpdate: (href: string) => void
  ) => {
    switch (s) {
      case BOOKER_TABS.HOTEL:
        return (
          <HotelTabContent
            onHrefUpdate={onHrefUpdate}
            href={href}
            onSearchCalled={handleSearchButtonClick}
            type={tabType}
            error={hotelError}
            initialError={initialHotelError}
            setError={setHotelError}
            setInitialError={setInitialHotelError}
            setGuestError={setHotelGuestError}
          />
        );

      case BOOKER_TABS.CYPRUS:
        return (
          <CyprusTabContent
            onHrefUpdate={onHrefUpdate}
            href={href}
            onSearchCalled={handleSearchButtonClick}
            type={tabType}
            error={packageError}
            initialError={initialPackageError}
            setError={setPackageError}
            setInitialError={setInitialPackageError}
            setGuestError={setPackageGuestError}
            isHomePage={true}
            isHoliday={false}
          />
        );
      case BOOKER_TABS.TOUR:
        return (
          <BookerWheretoGo
            onHrefUpdate={onHrefUpdate}
            subtitle='Yurt dışı turlar, Yurt içi turlar'
            href={href}
            onSearchCalled={() => {
              handleSearchButtonClick();
            }}
            type={tabType}
            error={tourError}
            initialError={initialTourError}
            setError={setTourError}
            setInitialError={setInitialTourError}
          />
        );
      case BOOKER_TABS.FLIGHT:
        return (
          <FlightTabContent
            onHrefUpdate={onHrefUpdate}
            error={flightError}
            initialError={initialFlightError}
            href={href}
            setError={setFlightError}
            setInitialError={setInitialFlightError}
            isBookerSearch
            onSearchCalled={isFlightOnSearchClicked}
            borderRadius='0'
            isHomePage={true}
            isHoliday={false}
          />
        );
      case BOOKER_TABS.CRUISE:
        return (
          <CruiseTabContent
            onHrefUpdate={onHrefUpdate}
            subtitle='Ülke, şehir, bölge, liman veya tur adı '
            href={href}
            onSearchCalled={handleSearchButtonClick}
            type={tabType}
            error={cruiseError}
            initialError={initialCruiseError}
            setError={setCruiseError}
            setInitialError={setInitialCruiseError}
          />
        );
    }
    return null;
  };
  const bookerRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick(bookerRef, () => {
    if (hotelError?.location) {
      setHotelError({});
    }
    if (tourError?.location) {
      setTourError({});
    }
    if (flightError?.location || flightError?.date) {
      setFlightError({});
    }
    if (cruiseError?.location) {
      setCruiseError({});
    }
    if (packageError?.location || packageError?.date) {
      setPackageError({});
    }
  });

  return (
    <HomePageBookerContainer bottom={0}>
      {isPackageNearestDateWarningVisible ? (
        <PackageNearestDateWarning
          onCloseModal={() => {
            setIsPackageNearestDateWarningVisible(false);
          }}
        />
      ) : null}
      <BookerContainer
        hasError={hasError}
        ref={bookerRef}
        bottom={isDesktop ? 0 : 80}
      >
        <TabsContainer hasCyprus={!!hasCyprusFeature}>
          <Tab selected={selected} onTabSelection={onTabSelect}>
            {BookerTabItems?.map((item, index) =>
              item.type === 'cyprus' && !hasCyprusFeature ? (
                <> </>
              ) : (
                <TabItem
                  key={index}
                  text={
                    item.type === 'cyprus' && !isDesktop
                      ? 'Kıbrıs Paket'
                      : item.title
                  }
                  icon={item.icon}
                />
              )
            )}
          </Tab>
        </TabsContainer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            alignItems: 'end',
          }}
        >
          {getSelectedContent(selected, onHrefUpdate)}
          <BookerSearchButton onClick={handleSearchButtonClick} />
        </div>
      </BookerContainer>
    </HomePageBookerContainer>
  );
};

export default Booker;
